/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import Skeleton from 'bundles/unified-home-common/components/shared/Skeleton';

type PropsFromCaller = {
  title?: React.ReactNode;
  description: React.ReactNode;
  children: React.ReactNode;
  isMultiChoice: boolean;
};

type Props = PropsFromCaller;

type PropsForPlaceholder = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  isMultiChoice?: boolean;
  shouldRenderItemDescriptions?: boolean;
};

const styles = {
  section: (theme: Theme) => ({
    margin: 'var(--cds-spacing-400) var(--cds-spacing-600) 0 var(--cds-spacing-600)',
    [breakpoints.down('xs')]: {
      margin: 'var(--cds-spacing-300) var(--cds-spacing-200) 0 var(--cds-spacing-200)',
    },
  }),
  description: (theme: Theme) => ({
    marginTop: 'var(--cds-spacing-100)',
  }),
  listItem: (theme: Theme) => ({
    display: 'flex',
    flexFlow: 'row nowrap',
    margin: 'var(--cds-spacing-200) 0',
    [breakpoints.down('xs')]: {
      margin: 'var(--cds-spacing-200) 0',
    },
  }),
  listItemRadio: {
    // Global CSS messes with this, so punch it with !important
    margin: '0 0 0 0 !important',
    zoom: 2,
    flexShrink: 0,
  },
  listItemLabelMultiChoice: (theme: Theme) => ({
    margin: '0 0 0 var(--cds-spacing-150)',
    flexGrow: 1,
  }),
  listItemLabel: {
    flexGrow: 1,
  },
};

const EnrollmentChoiceModalOptionListContainer = ({ title, description, isMultiChoice, children }: Props) => {
  const theme = useTheme();

  // ---

  return (
    <div css={styles.section(theme)}>
      {title && (
        <Typography2 variant="subtitleMedium" component="h3">
          {title}
        </Typography2>
      )}
      <Typography2 component="p" variant="bodyPrimary" css={styles.description(theme)}>
        {description}
      </Typography2>
      {isMultiChoice && (
        <div role="group" aria-labelledby="EnrollmentChoiceModal-GroupLabel">
          {children}
        </div>
      )}
      {!isMultiChoice && children}
    </div>
  );
};

const EnrollmentChoiceModalOptionListContainerPlaceholder = ({
  title = <Skeleton width="30%" />,
  description = (
    <>
      <Skeleton width="100%" />
      <Skeleton width="100%" />
      <Skeleton width="60%" />
    </>
  ),
  isMultiChoice = true,
  shouldRenderItemDescriptions = true,
}: PropsForPlaceholder) => {
  const theme = useTheme();

  // ---

  return (
    <EnrollmentChoiceModalOptionListContainer title={title} description={description} isMultiChoice={isMultiChoice}>
      {isMultiChoice ? (
        <>
          <div css={styles.listItem(theme)}>
            <input css={styles.listItemRadio} type="radio" tabIndex={-1} disabled />
            <span css={styles.listItemLabelMultiChoice(theme)}>
              <Typography2 component="span">
                <Skeleton width="25%" />
              </Typography2>
              {shouldRenderItemDescriptions && <br />}
              {shouldRenderItemDescriptions && (
                <Typography2 variant="bodySecondary" color="supportText" component="span">
                  <Skeleton width="50%" />
                </Typography2>
              )}
            </span>
          </div>
          <div css={styles.listItem(theme)}>
            <input css={styles.listItemRadio} type="radio" tabIndex={-1} disabled />
            <span css={styles.listItemLabelMultiChoice(theme)}>
              <Typography2 component="span">
                <Skeleton width="30%" />
              </Typography2>
              {shouldRenderItemDescriptions && <br />}
              {shouldRenderItemDescriptions && (
                <Typography2 variant="bodySecondary" color="supportText" component="span">
                  <Skeleton width="47%" />
                </Typography2>
              )}
            </span>
          </div>
          <div css={styles.listItem(theme)}>
            <input css={styles.listItemRadio} type="radio" tabIndex={-1} disabled />
            <span css={styles.listItemLabelMultiChoice(theme)}>
              <Typography2 component="span">
                <Skeleton width="22%" />
              </Typography2>
              {shouldRenderItemDescriptions && <br />}
              {shouldRenderItemDescriptions && (
                <Typography2 variant="bodySecondary" color="supportText" component="span">
                  <Skeleton width="53%" />
                </Typography2>
              )}
            </span>
          </div>
        </>
      ) : (
        <div css={styles.listItem(theme)}>
          <span css={styles.listItemLabel}>
            <Typography2 component="span">
              <Skeleton width="25%" />
            </Typography2>
            {shouldRenderItemDescriptions && <br />}
            {shouldRenderItemDescriptions && (
              <Typography2 variant="bodySecondary" color="supportText" component="span">
                <Skeleton width="50%" />
              </Typography2>
            )}
          </span>
        </div>
      )}
    </EnrollmentChoiceModalOptionListContainer>
  );
};

EnrollmentChoiceModalOptionListContainer.Placeholder = EnrollmentChoiceModalOptionListContainerPlaceholder;

export default EnrollmentChoiceModalOptionListContainer;
